import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTheme } from "next-themes";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/components/AuthProvider";

/**
 * User-level appearance preferences. The server row is the durable source of
 * truth so preferences follow the user across browsers; localStorage (the
 * `theme` and `dark-mode-depth` keys) is the cache that paints on first load
 * before this query resolves.
 */
export type AppearanceSettings = {
  theme: string | null;
  dark_mode_depth: number | null;
};
export const APPEARANCE_SETTINGS_QUERY_KEY = ["appearance-settings"] as const;
export function useAppearanceSettings(enabled) {
  const $ = _c(4);
  const api = useApi();
  let t0;
  if ($[0] !== api) {
    t0 = async () => {
      const response = await api.get("/user/appearance-settings");
      return response.data;
    };
    $[0] = api;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== enabled) {
    t1 = {
      enabled,
      staleTime: Number.POSITIVE_INFINITY
    };
    $[2] = enabled;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useQuery(APPEARANCE_SETTINGS_QUERY_KEY, t0, t1);
}
export function useUpdateAppearanceSettings() {
  const $ = _c(4);
  const api = useApi();
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== api) {
    t0 = async update => {
      const response = await api.put("/user/appearance-settings", update);
      return response.data;
    };
    $[0] = api;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== queryClient) {
    t1 = {
      onSuccess: data => {
        queryClient.setQueryData(APPEARANCE_SETTINGS_QUERY_KEY, data);
      }
    };
    $[2] = queryClient;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return useMutation(t0, t1);
}

/**
 * Sets the app theme and persists it as the user's saved preference, so
 * theme changes made anywhere (sidebar toggle, command palette) survive the
 * server reconciliation on the next load.
 */
export function useSetPersistedTheme() {
  const $ = _c(4);
  const {
    setTheme
  } = useTheme();
  const {
    user
  } = useAuth();
  const {
    mutate: persistSettings
  } = useUpdateAppearanceSettings();
  let t0;
  if ($[0] !== persistSettings || $[1] !== setTheme || $[2] !== user) {
    t0 = theme => {
      setTheme(theme);
      if (user) {
        persistSettings({
          theme
        });
      }
    };
    $[0] = persistSettings;
    $[1] = setTheme;
    $[2] = user;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}